import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import Papa from 'papaparse';
import { v4 as uuidv4 } from 'uuid';
import { sanitizeInput, escapeCSVValue } from '../utils/sanitize';
import { DEFAULT_FINDINGS } from './defaultFindingsData';
import { COMPREHENSIVE_FINDINGS } from './comprehensiveAssessmentData';

const SEEDED_FINDINGS = [...DEFAULT_FINDINGS, ...COMPREHENSIVE_FINDINGS];

/**
 * Findings Store
 * Manages security findings/gaps discovered during assessments.
 * Aligned with Jira FND (Findings) project structure.
 */

const useFindingsStore = create(
  persist(
    (set, get) => ({
      findings: SEEDED_FINDINGS,
      loading: false,
      error: null,

      // Get all findings
      getFindings: () => get().findings,

      // Get finding by ID
      getFinding: (findingId) => {
        return get().findings.find(f => f.id === findingId);
      },

      // Get findings by compliance requirement
      getFindingsByRequirement: (requirementId) => {
        return get().findings.filter(f => f.complianceRequirement === requirementId);
      },

      // Get findings by control
      getFindingsByControl: (controlId) => {
        return get().findings.filter(f => f.controlId === controlId);
      },

      // Get findings by evaluation (NEW - primary link for point-in-time findings)
      getFindingsByEvaluation: (evaluationId) => {
        return get().findings.filter(f => f.evaluationId === evaluationId);
      },

      // Get findings by assessment (via evaluations)
      getFindingsByAssessment: (assessmentId) => {
        return get().findings.filter(f =>
          f.assessmentId === assessmentId ||
          (f.evaluationId && f.evaluationId.includes(assessmentId))
        );
      },

      // Get findings by status
      getFindingsByStatus: (status) => {
        return get().findings.filter(f => f.status === status);
      },

      // Get findings by priority
      getFindingsByPriority: (priority) => {
        return get().findings.filter(f => f.priority === priority);
      },

      // Create new finding
      createFinding: (findingData) => {
        const newFinding = {
          id: `FND-${uuidv4()}`,
          summary: sanitizeInput(findingData.summary || ''),

          // Primary link: Evaluation (point-in-time assessment record)
          evaluationId: findingData.evaluationId || null,

          // Secondary/cached links (derived from evaluation when possible)
          controlId: findingData.controlId || null,
          assessmentId: findingData.assessmentId || null,

          // DEPRECATED: Use evaluationId → controlId → linkedRequirementIds instead
          complianceRequirement: findingData.complianceRequirement || null,

          rootCause: sanitizeInput(findingData.rootCause || ''),
          remediationActionPlan: sanitizeInput(findingData.remediationActionPlan || ''),
          remediationOwner: findingData.remediationOwner || null,
          dueDate: findingData.dueDate || '',
          status: findingData.status || 'Not Started', // Not Started, In Progress, Resolved
          priority: findingData.priority || 'Medium', // Low, Medium, High, Critical
          createdDate: new Date().toISOString(),
          lastModified: new Date().toISOString(),
          jiraKey: findingData.jiraKey || null, // Jira issue key if synced
          linkedArtifacts: findingData.linkedArtifacts || []
        };

        set((state) => ({
          findings: [...state.findings, newFinding]
        }));

        return newFinding;
      },

      // Update finding
      updateFinding: (findingId, updates) => {
        const sanitizedUpdates = {
          ...updates,
          lastModified: new Date().toISOString()
        };

        if (updates.summary !== undefined) {
          sanitizedUpdates.summary = sanitizeInput(updates.summary);
        }
        if (updates.rootCause !== undefined) {
          sanitizedUpdates.rootCause = sanitizeInput(updates.rootCause);
        }
        if (updates.remediationActionPlan !== undefined) {
          sanitizedUpdates.remediationActionPlan = sanitizeInput(updates.remediationActionPlan);
        }

        set((state) => ({
          findings: state.findings.map(f =>
            f.id === findingId ? { ...f, ...sanitizedUpdates } : f
          )
        }));
      },

      // Delete finding
      deleteFinding: (findingId) => {
        set((state) => ({
          findings: state.findings.filter(f => f.id !== findingId)
        }));
      },

      // Link artifact to finding
      linkArtifact: (findingId, artifactId) => {
        set((state) => ({
          findings: state.findings.map(f => {
            if (f.id === findingId) {
              const artifacts = f.linkedArtifacts || [];
              if (!artifacts.includes(artifactId)) {
                return { ...f, linkedArtifacts: [...artifacts, artifactId] };
              }
            }
            return f;
          })
        }));
      },

      // Unlink artifact from finding
      unlinkArtifact: (findingId, artifactId) => {
        set((state) => ({
          findings: state.findings.map(f => {
            if (f.id === findingId) {
              return {
                ...f,
                linkedArtifacts: (f.linkedArtifacts || []).filter(id => id !== artifactId)
              };
            }
            return f;
          })
        }));
      },

      // Set Jira key (for sync tracking)
      setJiraKey: (findingId, jiraKey) => {
        get().updateFinding(findingId, { jiraKey });
      },

      // Get findings statistics
      getStatistics: () => {
        const findings = get().findings;
        return {
          total: findings.length,
          byStatus: {
            notStarted: findings.filter(f => f.status === 'Not Started').length,
            inProgress: findings.filter(f => f.status === 'In Progress').length,
            resolved: findings.filter(f => f.status === 'Resolved').length
          },
          byPriority: {
            low: findings.filter(f => f.priority === 'Low').length,
            medium: findings.filter(f => f.priority === 'Medium').length,
            high: findings.filter(f => f.priority === 'High').length,
            critical: findings.filter(f => f.priority === 'Critical').length
          },
          overdue: findings.filter(f => {
            if (!f.dueDate || f.status === 'Resolved') return false;
            return new Date(f.dueDate) < new Date();
          }).length
        };
      },

      // Export findings to CSV (standard format)
      exportFindingsCSV: (userStore) => {
        const findings = get().findings;
        const users = userStore?.getState?.()?.users || [];

        const getUserName = (userId) => {
          const user = users.find(u => u.id === userId);
          if (!user) return userId || '';
          return user.email ? `${user.name} <${user.email}>` : user.name;
        };

        const csvData = findings.map(f => ({
          'Finding ID': f.id,
          'Summary': escapeCSVValue(f.summary),
          'Evaluation ID': f.evaluationId || '',
          'Control ID': escapeCSVValue(f.controlId || ''),
          'Assessment ID': f.assessmentId || '',
          'Compliance Requirement': escapeCSVValue(f.complianceRequirement || ''), // Deprecated
          'Root Cause': escapeCSVValue(f.rootCause),
          'Remediation Action Plan': escapeCSVValue(f.remediationActionPlan),
          'Remediation Owner': escapeCSVValue(getUserName(f.remediationOwner)),
          'Due Date': f.dueDate,
          'Status': f.status,
          'Priority': f.priority,
          'Created Date': f.createdDate,
          'Jira Key': f.jiraKey || '',
          'Linked Artifacts': escapeCSVValue((f.linkedArtifacts || []).join('; '))
        }));

        const csv = Papa.unparse(csvData);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        const date = new Date().toISOString().split('T')[0];

        link.setAttribute('href', url);
        link.setAttribute('download', `findings_${date}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      },

      // Export to Jira FND project format
      exportForJiraCSV: (userStore) => {
        const findings = get().findings;
        const users = userStore?.getState?.()?.users || [];

        const getUserEmail = (userId) => {
          const user = users.find(u => u.id === userId);
          return user?.email || '';
        };

        // Jira CSV import format for FND project
        const csvData = findings.map(f => ({
          'Summary': escapeCSVValue(f.summary),
          'Issue Type': 'Finding',
          'Project key': 'FND',
          'Priority': f.priority,
          'Assignee': escapeCSVValue(getUserEmail(f.remediationOwner)),
          'Due date': f.dueDate,
          'Custom field (Evaluation ID)': f.evaluationId || '',
          'Custom field (Control ID)': escapeCSVValue(f.controlId || ''),
          'Custom field (Compliance Requirement)': escapeCSVValue(f.complianceRequirement || ''), // Deprecated
          'Custom field (Root Cause)': escapeCSVValue(f.rootCause),
          'Custom field (Remediation Action Plan (Who will do What by When?))': escapeCSVValue(f.remediationActionPlan),
          'Description': escapeCSVValue(`Finding created from CSF Profile assessment.\n\nEvaluation: ${f.evaluationId || 'N/A'}\nControl: ${f.controlId || 'N/A'}\n\nRoot Cause:\n${f.rootCause}\n\nRemediation Plan:\n${f.remediationActionPlan}`)
        }));

        const csv = Papa.unparse(csvData);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        const date = new Date().toISOString().split('T')[0];

        link.setAttribute('href', url);
        link.setAttribute('download', `jira_fnd_import_${date}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      },

      // Import findings from CSV
      importFindingsCSV: async (csvText, userStore) => {
        return new Promise((resolve, reject) => {
          Papa.parse(csvText, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
              const findOrCreateUser = userStore?.getState?.()?.findOrCreateUser;

              const parseUserString = (str) => {
                if (!str || !str.trim()) return null;
                str = str.trim();
                const match = str.match(/^(.+?)\s*<([^>]+)>$/);
                if (match) {
                  return { name: match[1].trim(), email: match[2].trim() };
                }
                if (str.includes('@')) {
                  return { name: str.split('@')[0].replace(/[._]/g, ' '), email: str };
                }
                return { name: str, email: null };
              };

              const newFindings = results.data.map(row => {
                let remediationOwner = null;
                const ownerStr = row['Remediation Owner'] || row['Assignee'] || row['Reporter'];
                if (ownerStr && findOrCreateUser) {
                  const info = parseUserString(ownerStr);
                  if (info) remediationOwner = findOrCreateUser(info);
                }

                // Map Jira status to our status
                const mapStatus = (status) => {
                  if (!status) return 'Not Started';
                  const s = status.toLowerCase();
                  if (s === 'to do' || s === 'not started') return 'Not Started';
                  if (s === 'in progress') return 'In Progress';
                  if (s === 'done' || s === 'resolved' || s === 'closed') return 'Resolved';
                  return status;
                };

                return {
                  id: row['Issue key'] || row['Finding ID'] || `FND-${uuidv4()}`,
                  summary: sanitizeInput(row['Summary'] || ''),
                  description: sanitizeInput(row['Description'] || ''),

                  // Primary link: Evaluation
                  evaluationId: row['Evaluation ID'] || row['Custom field (Evaluation ID)'] || null,

                  // Secondary/cached links
                  controlId: row['Control ID'] || row['Custom field (Control ID)'] || null,
                  assessmentId: row['Assessment ID'] || null,

                  // Deprecated
                  complianceRequirement: row['Compliance Requirement'] || row['Custom field (Compliance Requirement)'] || null,

                  rootCause: sanitizeInput(row['Root Cause'] || row['Custom field (Root Cause)'] || row['Custom field (Root Case)'] || ''),
                  remediationActionPlan: sanitizeInput(
                    row['Remediation Action Plan'] ||
                    row['Custom field (Remediation Action Plan (Who will do What by When?))'] || ''
                  ),
                  remediationOwner,
                  dueDate: row['Due Date'] || row['Due date'] || '',
                  status: mapStatus(row['Status']),
                  priority: row['Priority'] || 'Medium',
                  createdDate: row['Created Date'] || row['Created'] || new Date().toISOString(),
                  lastModified: row['Updated'] || new Date().toISOString(),
                  jiraKey: row['Issue key'] || row['Jira Key'] || null,
                  linkedArtifacts: (row['Linked Artifacts'] || '')
                    .split(';').map(s => s.trim()).filter(Boolean)
                };
              });

              set((state) => ({
                findings: [...state.findings, ...newFindings]
              }));

              resolve(newFindings.length);
            },
            error: (error) => {
              reject(new Error('Failed to import CSV file. Please verify the file format.'));
            }
          });
        });
      },

      // Set all findings (for import/reset)
      setFindings: (findings) => {
        set({ findings });
      }
    }),
    {
      name: 'csf-findings-storage',
      version: 4,
      migrate: (persistedState, version) => {
        if (version < 2) {
          if (persistedState?.findings?.length > 0) {
            return persistedState;
          }
          return { findings: SEEDED_FINDINGS };
        }
        if (version < 3) {
          return { findings: SEEDED_FINDINGS };
        }
        // Version 4: Merge in catalog findings for the comprehensive assessment
        if (version < 4) {
          const existing = persistedState?.findings || [];
          const existingIds = new Set(existing.map(f => f.id));
          const additions = COMPREHENSIVE_FINDINGS.filter(f => !existingIds.has(f.id));
          return { ...persistedState, findings: [...existing, ...additions] };
        }
        return persistedState;
      },
      partialize: (state) => ({
        findings: state.findings
      })
    }
  )
);

export default useFindingsStore;
